import * as React from 'react';
// Import material ui modules.
import {
	Check,
	Close,
} from '@mui/icons-material';
// Import hooks.
import {
	useInputDate,
	UseInputDateResults,
	useInputDropdown,
	UseInputDropdownResults,
	useInputEmail,
	UseInputEmailResults,
	UseInputFormResult,
	useInputPhone,
	UseInputPhoneResults,
	useInputText,
	UseInputTextResults,
	useInputYesNo,
	UseInputYesNoResults,
} from '@app/hooks/view/input';
import {
	useFormPopupAddPolicyNoteBase,
	UseFormPopupAddPolicyNoteBaseResults,
} from '@app/hooks/view';
// Import props.
import {
	FormPopupProps,
	NavigationFooterProps,
	FormPopupAddPolicyNoteAdminProps,
	ScrollbarProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	NavigationFooterAlignment,
	PopupWidth,
} from '@app/enumerations';
// Import api models.
import {
	PolicyNote,
	PossibleCorrespondenceReason,
	PossibleCorrespondenceRecvdOrSent,
	PossibleCorrespondenceSubject,
	PossibleCorrespondenceType,
	PossibleCorrespondenceWho,
} from '@app/models/api';
// Import app models.
import {
	DropdownItem,
} from '@app/models/app';

interface UseFormPopupAddPolicyNoteAdminParameters {
	id: string;
	props: FormPopupAddPolicyNoteAdminProps,
}

interface UseFormPopupAddPolicyNoteAdminResults {
	submitForm: () => void;
	adminOnlyInput: UseInputYesNoResults;
	isFlagInput: UseInputYesNoResults;
	flagDateInput: UseInputDateResults;
	formPopupAddPolicyNoteBase: UseFormPopupAddPolicyNoteBaseResults;
	correspondenceRecvdOrSentInput: UseInputDropdownResults;
	correspondenceTypeInput: UseInputDropdownResults;
	correspondenceWhoInput: UseInputDropdownResults;
	correspondenceSubjectInput: UseInputDropdownResults;
	correspondenceReasonInput: UseInputDropdownResults;
	complaintInitiatedInput: UseInputYesNoResults;
	correspondenceNameInput: UseInputTextResults;
	correspondenceEmailInput: UseInputEmailResults;
	correspondencePhone1Input: UseInputPhoneResults;
	correspondencePhone2Input: UseInputPhoneResults;
	applyFormProps: () => FormPopupProps & ScrollbarProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
}

const useFormPopupAddPolicyNoteAdmin = (params: UseFormPopupAddPolicyNoteAdminParameters): UseFormPopupAddPolicyNoteAdminResults => {

	// #region exported methods

	/**
	 *  Starts the submission process with validation for the address.
	 **/
	const submitForm = (): void => {
		// First flag all inputs to show any validation errors.
		flagAllInputsToShowValidations();
		// Validate the whole form.
		let isValid: boolean = checkFormValidationWithFocus();
		// If form passes validation, proceed to submission.
		if (isValid) {
			params.props.onSave(createPolicyNote(), formPopupAddPolicyNoteBase.createPolicyNoteDocument());
		}
	}

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormPopupProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: 'Add Note',
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: true,
			isOpen: params.props.isOpen,
			popupWidth: PopupWidth.EXTRA_LARGE,
			onCloseClick: params.props.onClose,
		};
	};

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Close',
			leftButtonClick: params.props.onClose,
			leftButtonIcon: Close,
			rightButtonDisabled: false,
			rightButtonLabel: 'Save',
			rightButtonClick: submitForm,
			rightButtonIcon: Check,
			alignment: NavigationFooterAlignment.RIGHT,
		};
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Checks to see if the flag date is required. 
	 **/
	const isFlagDateRequired = (): boolean => {
		return isFlagInput.value !== undefined ? isFlagInput.value : false;
	}

	/**
	 * Checks to see if the correspondence info is required. 
	 **/
	const isCorrespondenceInfoRequired = (): boolean => {
		if (String.isNullOrWhiteSpace(correspondenceRecvdOrSentInput.value)) {
			return false;
		}

		// Find matching correspondence recvd or sent.
		let selectedPossibleCorrespondenceRecvdOrSent: PossibleCorrespondenceRecvdOrSent | undefined = params.props
			.possibleCorrespondenceRecvdOrSent
			.find((possibleCorrespondenceRecvdOrSent: PossibleCorrespondenceRecvdOrSent) => possibleCorrespondenceRecvdOrSent.code === correspondenceRecvdOrSentInput.value);

		// If there is a selected correspondenc recvd or sent record, return whether the correspondence info is required or not.
		return selectedPossibleCorrespondenceRecvdOrSent?.isCorrespondenceInfoRequired ?? false;
	}

	/**
	 * Converts the correspondence-received-or-sent options to a list of dropdown items. 
	 **/
	const getCorrespondenceRecvdOrSentItems = (): DropdownItem[] => {
		return params.props.possibleCorrespondenceRecvdOrSent.map((possibleCorrespondenceRecvdOrSent: PossibleCorrespondenceRecvdOrSent) => {
			return new DropdownItem({
				label: possibleCorrespondenceRecvdOrSent.code !== null ? possibleCorrespondenceRecvdOrSent.code.toString() : String.empty(),
				value: possibleCorrespondenceRecvdOrSent.code !== null ? possibleCorrespondenceRecvdOrSent.code.toString() : String.empty(),
			});
		})
	}

	/**
	 * Converts the correspondence-type options to a list of dropdown items. 
	 **/
	const getCorrespondenceTypeItems = (): DropdownItem[] => {
		return params.props.possibleCorrespondenceTypes.map((possibleCorrespondenceType: PossibleCorrespondenceType) => {
			return new DropdownItem({
				label: possibleCorrespondenceType.code !== null ? possibleCorrespondenceType.code.toString() : String.empty(),
				value: possibleCorrespondenceType.code !== null ? possibleCorrespondenceType.code.toString() : String.empty(),
			});
		})
	}

	/**
	 * Converts the correspondence-who options to a list of dropdown items. 
	 **/
	const getCorrespondenceWhoItems = (): DropdownItem[] => {
		return params.props.possibleCorrespondenceWho.map((possibleCorrespondenceWho: PossibleCorrespondenceWho) => {
			return new DropdownItem({
				label: possibleCorrespondenceWho.code !== null ? possibleCorrespondenceWho.code.toString() : String.empty(),
				value: possibleCorrespondenceWho.code !== null ? possibleCorrespondenceWho.code.toString() : String.empty(),
			});
		})
	}

	/**
	 * Converts the correspondence-subject options to a list of dropdown items. 
	 **/
	const getCorrespondenceSubjectItems = (): DropdownItem[] => {
		return params.props.possibleCorrespondenceSubjects.map((possibleCorrespondenceSubject: PossibleCorrespondenceSubject) => {
			return new DropdownItem({
				label: possibleCorrespondenceSubject.code !== null ? possibleCorrespondenceSubject.code.toString() : String.empty(),
				value: possibleCorrespondenceSubject.code !== null ? possibleCorrespondenceSubject.code.toString() : String.empty(),
			});
		})
	}

	/**
	 * Converts the correspondence-reason options to a list of dropdown items. 
	 **/
	const getCorrespondenceReasonItems = (): DropdownItem[] => {
		// Return nothing if no correspondence subjected has been selected.
		if (String.isNullOrWhiteSpace(correspondenceSubjectInput.value)) {
			return [];
		}

		// Get selected correspondence subject.
		let selectedCorrespondenceSubject: PossibleCorrespondenceSubject | undefined = params.props
			.possibleCorrespondenceSubjects
			.find((possibleCorrespondenceSubject: PossibleCorrespondenceSubject) => possibleCorrespondenceSubject.code === correspondenceSubjectInput.value);

		// If there is no selected correspondence subject, return nothing.
		if (selectedCorrespondenceSubject === undefined) {
			return [];
		}

		return selectedCorrespondenceSubject.correspondenceReasons.map((possibleCorrespondenceReason: PossibleCorrespondenceReason) => {
			return new DropdownItem({
				label: possibleCorrespondenceReason.code !== null ? possibleCorrespondenceReason.code.toString() : String.empty(),
				value: possibleCorrespondenceReason.code !== null ? possibleCorrespondenceReason.code.toString() : String.empty(),
			});
		});
	}

	/**
	 * Fetches the default correspondence-received-or-sent as dropdown item. 
	 **/
	const getDefaultCorrespondenceRecvdOrSentDropdownItem = (): DropdownItem => {
		return new DropdownItem({
			label: '-- Select --',
			value: '',
		})
	}

	/**
	 * Fetches the default correspondence-type as dropdown item. 
	 **/
	const getDefaultCorrespondenceTypeDropdownItem = (): DropdownItem => {
		return new DropdownItem({
			label: '-- Select --',
			value: '',
		})
	}

	/**
	 * Fetches the default correspondence-who as dropdown item. 
	 **/
	const getDefaultCorrespondenceWhoDropdownItem = (): DropdownItem => {
		return new DropdownItem({
			label: '-- Select --',
			value: '',
		})
	}

	/**
	 * Fetches the default correspondence-subject as dropdown item. 
	 **/
	const getDefaultCorrespondenceSubjectDropdownItem = (): DropdownItem => {
		return new DropdownItem({
			label: '-- Select --',
			value: '',
		})
	}

	/**
	 * Fetches the default correspondence-reason as dropdown item. 
	 **/
	const getDefaultCorrespondenceReasonDropdownItem = (): DropdownItem => {
		return new DropdownItem({
			label: '-- Select --',
			value: '',
		})
	}

	/**
	 * Creates a policy note record by collecting all user inputs.
	 **/
	const createPolicyNote = (): PolicyNote => {
		let policyNote: PolicyNote = new PolicyNote();
		policyNote.adminOnly = adminOnlyInput.value ?? null;
		policyNote.isFlag = isFlagInput.value ?? null;
		policyNote.flagDate = flagDateInput.value ?? null;
		policyNote.note = formPopupAddPolicyNoteBase.noteInput.value;
		policyNote.correspondenceRecvdOrSent = !String.isNullOrWhiteSpace(correspondenceRecvdOrSentInput.value) ? correspondenceRecvdOrSentInput.value : null;
		policyNote.correspondenceType = !String.isNullOrWhiteSpace(correspondenceTypeInput.value) ? correspondenceTypeInput.value : null;
		policyNote.correspondenceWho = !String.isNullOrWhiteSpace(correspondenceWhoInput.value) ? correspondenceWhoInput.value : null;
		policyNote.correspondenceSubject = !String.isNullOrWhiteSpace(correspondenceSubjectInput.value) ? correspondenceSubjectInput.value : null;
		policyNote.correspondenceReasons = !String.isNullOrWhiteSpace(correspondenceReasonInput.value) ? [correspondenceReasonInput.value] : [];
		policyNote.complaintInitiated = complaintInitiatedInput.value ?? null;
		policyNote.correspondenceName = !String.isNullOrWhiteSpace(correspondenceNameInput.value) ? correspondenceNameInput.value : null;
		policyNote.correspondenceEmailAddress = !String.isNullOrWhiteSpace(correspondenceEmailInput.value) ? correspondenceEmailInput.value : null;
		policyNote.correspondencePhone1 = !String.isNullOrWhiteSpace(correspondencePhone1Input.value) ? correspondencePhone1Input.value : null;
		policyNote.correspondencePhone2 = !String.isNullOrWhiteSpace(correspondencePhone2Input.value) ? correspondencePhone2Input.value : null;
		return policyNote;
	}

	/**
	 * Validates an individual input with scrolling.
	 * @param input
	 */
	const checkInputValidationWithFocus = <T>(input: UseInputFormResult<T>): boolean => {
		let isValid = input.isInputValid;
		if (!isValid) {
			input.focusToInput();
		}
		return isValid;
	}

	/**
	 * Method to check form validation with focus. 
	 **/
	const checkFormValidationWithFocus = (): boolean => {
		return checkInputValidationWithFocus<boolean | undefined>(adminOnlyInput)
			&& checkInputValidationWithFocus<boolean | undefined>(isFlagInput)
			&& checkInputValidationWithFocus<Date | undefined>(flagDateInput)
			&& formPopupAddPolicyNoteBase.checkValidationWithFocus()
			&& checkInputValidationWithFocus<string>(correspondenceRecvdOrSentInput)
			&& checkInputValidationWithFocus<string>(correspondenceTypeInput)
			&& checkInputValidationWithFocus<string>(correspondenceWhoInput)
			&& checkInputValidationWithFocus<string>(correspondenceSubjectInput)
			&& checkInputValidationWithFocus<string>(correspondenceReasonInput)
			&& checkInputValidationWithFocus<boolean | undefined>(complaintInitiatedInput)
			&& checkInputValidationWithFocus<string>(correspondenceNameInput)
			&& checkInputValidationWithFocus<string>(correspondenceEmailInput)
			&& checkInputValidationWithFocus<string>(correspondencePhone1Input)
			&& checkInputValidationWithFocus<string>(correspondencePhone2Input);
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		adminOnlyInput.changeShowValidationFlag(true);
		isFlagInput.changeShowValidationFlag(true);
		flagDateInput.changeShowValidationFlag(true);
		formPopupAddPolicyNoteBase.flagAllInputsToShowValidations();
		correspondenceRecvdOrSentInput.changeShowValidationFlag(true);
		correspondenceTypeInput.changeShowValidationFlag(true);
		correspondenceWhoInput.changeShowValidationFlag(true);
		correspondenceSubjectInput.changeShowValidationFlag(true);
		correspondenceReasonInput.changeShowValidationFlag(true);
		complaintInitiatedInput.changeShowValidationFlag(true);
		correspondenceNameInput.changeShowValidationFlag(true);
		correspondenceEmailInput.changeShowValidationFlag(true);
		correspondencePhone1Input.changeShowValidationFlag(true);
		correspondencePhone2Input.changeShowValidationFlag(true);
	}

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	/**
	 * Generate id for admin only. 
	 **/
	const generateIdForAdminOnly = (): string => {
		return `${generateIdForForm()}-adminOnly`;
	}

	/**
	 * Generate id for is flag. 
	 **/
	const generateIdForIsFlag = (): string => {
		return `${generateIdForForm()}-isFlag`;
	}

	/**
	 * Generate id for flag date. 
	 **/
	const generateIdForFlagDate = (): string => {
		return `${generateIdForForm()}-flagDate`;
	}

	/**
	 * Generate id for correspondence recvd or sent 
	 **/
	const generateIdForCorrespondenceRecvdOrSent = (): string => {
		return `${generateIdForForm()}-correspondenceRecvdOrSent`;
	}

	/**
	 * Generate id for correspondence type
	 **/
	const generateIdForCorrespondenceType = (): string => {
		return `${generateIdForForm()}-correspondenceType`;
	}

	/**
	 * Generate id for correspondence who
	 **/
	const generateIdForCorrespondenceWho = (): string => {
		return `${generateIdForForm()}-correspondenceWho`;
	}

	/**
	 * Generate id for correspondence subject
	 **/
	const generateIdForCorrespondenceSubject = (): string => {
		return `${generateIdForForm()}-correspondenceSubject`;
	}

	/**
	 * Generate id for correspondence reason
	 **/
	const generateIdForCorrespondenceReason = (): string => {
		return `${generateIdForForm()}-correspondenceReason`;
	}

	/**
	 * Generate id for complaint initiated
	 **/
	const generateIdForComplaintInitiated = (): string => {
		return `${generateIdForForm()}-complaintInitiated`;
	}

	/**
	 * Generate id for correspondence name
	 **/
	const generateIdForCorrespondenceName = (): string => {
		return `${generateIdForForm()}-correspondenceName`;
	}

	/**
	 * Generate id for correspondence email
	 **/
	const generateIdForCorrespondenceEmail = (): string => {
		return `${generateIdForForm()}-correspondenceEmail`;
	}

	/**
	 * Generate id for correspondence phone 1
	 **/
	const generateIdForCorrespondencePhone1 = (): string => {
		return `${generateIdForForm()}-correspondencePhone1`;
	}

	/**
	 * Generate id for correspondence phone 2
	 **/
	const generateIdForCorrespondencePhone2 = (): string => {
		return `${generateIdForForm()}-correspondencePhone2`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	// Instantiate the 'form popup add policy note base' hook.
	const formPopupAddPolicyNoteBase: UseFormPopupAddPolicyNoteBaseResults = useFormPopupAddPolicyNoteBase({
		id: generateIdForForm(),
		possibleDocCategories: params.props.possibleDocCategories,
		policyTermYears: params.props.policyTermYears,
	});

	// Instantiate the 'admin only' hook.
	let adminOnlyInput: UseInputYesNoResults = useInputYesNo({
		id: generateIdForAdminOnly(),
		label: 'Admin Only?',
		required: true,
		disabled: false,
		isYesChecked: undefined,
		showValidation: false,
	});

	// Instantiate the 'is flag' hook.
	let isFlagInput: UseInputYesNoResults = useInputYesNo({
		id: generateIdForIsFlag(),
		label: 'Flag Policy/Indiciation?',
		required: true,
		disabled: false,
		isYesChecked: undefined,
		showValidation: false,
	});

	// Instantiate the 'flag date' input hook.
	const flagDateInput: UseInputDateResults = useInputDate({
		id: generateIdForFlagDate(),
		label: 'Flag Date',
		validationLabel: 'flag date',
		required: true,
		disabled: isFlagInput.value !== undefined ? !isFlagInput.value : true,
		value: undefined,
		showValidation: false,
	});	

	// Instantiate the 'correspondence-received-or-sent' input hook.
	const correspondenceRecvdOrSentInput: UseInputDropdownResults = useInputDropdown({
		id: generateIdForCorrespondenceRecvdOrSent(),
		label: 'Correspondence Received',
		required: true,
		disabled: false,
		value: String.empty(),
		defaultDropdownItem: getDefaultCorrespondenceRecvdOrSentDropdownItem(),
		dropdownItems: getCorrespondenceRecvdOrSentItems(),
		showValidation: false,
	});

	// Instantiate the 'correspondence-type' input hook.
	const correspondenceTypeInput: UseInputDropdownResults = useInputDropdown({
		id: generateIdForCorrespondenceType(),
		label: 'Correspondence Type',
		required: isCorrespondenceInfoRequired(),
		disabled: !isCorrespondenceInfoRequired(),
		value: String.empty(),
		defaultDropdownItem: getDefaultCorrespondenceTypeDropdownItem(),
		dropdownItems: getCorrespondenceTypeItems(),
		showValidation: false,
	});

	// Instantiate the 'correspondence-who' input hook.
	const correspondenceWhoInput: UseInputDropdownResults = useInputDropdown({
		id: generateIdForCorrespondenceWho(),
		label: 'Correspondence With',
		required: isCorrespondenceInfoRequired(),
		disabled: !isCorrespondenceInfoRequired(),
		value: String.empty(),
		defaultDropdownItem: getDefaultCorrespondenceWhoDropdownItem(),
		dropdownItems: getCorrespondenceWhoItems(),
		showValidation: false,
	});

	// Instantiate the 'correspondence-subject' input hook.
	const correspondenceSubjectInput: UseInputDropdownResults = useInputDropdown({
		id: generateIdForCorrespondenceSubject(),
		label: 'Correspondence Subject',
		required: isCorrespondenceInfoRequired(),
		disabled: !isCorrespondenceInfoRequired(),
		value: String.empty(),
		defaultDropdownItem: getDefaultCorrespondenceSubjectDropdownItem(),
		dropdownItems: getCorrespondenceSubjectItems(),
		showValidation: false,
	});

	// Instantiate the 'correspondence-reason' input hook.
	const correspondenceReasonInput: UseInputDropdownResults = useInputDropdown({
		id: generateIdForCorrespondenceReason(),
		label: 'Reason',
		required: isCorrespondenceInfoRequired(),
		disabled: !isCorrespondenceInfoRequired(),
		value: String.empty(),
		defaultDropdownItem: getDefaultCorrespondenceReasonDropdownItem(),
		dropdownItems: getCorrespondenceReasonItems(),
		showValidation: false,
	});

	// Instantiate the 'complaint initiated' hook.
	let complaintInitiatedInput: UseInputYesNoResults = useInputYesNo({
		id: generateIdForComplaintInitiated(),
		label: 'Complaint Initiated?',
		required: isCorrespondenceInfoRequired(),
		disabled: !isCorrespondenceInfoRequired(),
		isYesChecked: undefined,
		showValidation: false,
	});

	// Instantiate the 'correspondence name' input hook.
	const correspondenceNameInput: UseInputTextResults = useInputText({
		id: generateIdForCorrespondenceName(),
		label: 'Correspondence Name',
		required: false,
		disabled: !isCorrespondenceInfoRequired(),
		value: String.empty(),
		showValidation: false,
	});

	// Instantiate the 'correspondence email' input hook.
	const correspondenceEmailInput: UseInputEmailResults = useInputEmail({
		id: generateIdForCorrespondenceEmail(),
		label: 'Correspondence Email',
		required: false,
		disabled: !isCorrespondenceInfoRequired(),
		value: String.empty(),
		showValidation: false,
	});

	// Instantiate the 'correspondence phone 1' input hook.
	const correspondencePhone1Input: UseInputPhoneResults = useInputPhone({
		id: generateIdForCorrespondencePhone1(),
		label: 'Correspondence Phone 1',
		required: false,
		disabled: !isCorrespondenceInfoRequired(),
		value: String.empty(),
		showValidation: false,
	});

	// Instantiate the 'correspondence phone 2' input hook.
	const correspondencePhone2Input: UseInputPhoneResults = useInputPhone({
		id: generateIdForCorrespondencePhone2(),
		label: 'Correspondence Phone 2',
		required: false,
		disabled: !isCorrespondenceInfoRequired(),
		value: String.empty(),
		showValidation: false,
	});

	// #endregion variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, or when the is-flag input is updated.
	React.useEffect(() => {
		// Reset the flag date if the note is no longer marked as flagged.
		if (!isFlagDateRequired()) {
			flagDateInput.setInput(undefined);
		}
	}, [isFlagInput.value]);

	// Called when the hook is initially mounted, or when the correspondence-received-or-sent input is updated.
	React.useEffect(() => {
		// Reset all the correspondence info inputs if they're not longer required.
		if (!isCorrespondenceInfoRequired()) {
			correspondenceTypeInput.setInput(String.empty());
			correspondenceWhoInput.setInput(String.empty());
			correspondenceSubjectInput.setInput(String.empty());
			correspondenceReasonInput.setInput(String.empty());
			complaintInitiatedInput.setInput(undefined);
			correspondenceNameInput.setInput(String.empty());
			correspondenceEmailInput.setInput(String.empty());
			correspondencePhone1Input.setInput(String.empty());
			correspondencePhone2Input.setInput(String.empty());
		}
	}, [correspondenceRecvdOrSentInput.value]);

	// Called when the hook is initially mounted, or when the correspondence-subject input is updated.
	React.useEffect(() => {
		// Reset the correspondence reason if the subject is updated.
		if (correspondenceReasonInput.doesInputHaveChanges) {
			correspondenceReasonInput.setInput(String.empty());
		}
	}, [correspondenceSubjectInput.value]);

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		// Focus to the first element by default.
		adminOnlyInput.focusToInput();
	}, []);

	// #endregion life-cycle methods.


	return {
		submitForm: submitForm,
		adminOnlyInput: adminOnlyInput,
		isFlagInput: isFlagInput,
		flagDateInput: flagDateInput,
		formPopupAddPolicyNoteBase: formPopupAddPolicyNoteBase,
		correspondenceRecvdOrSentInput: correspondenceRecvdOrSentInput,
		correspondenceTypeInput: correspondenceTypeInput,
		correspondenceWhoInput: correspondenceWhoInput,
		correspondenceSubjectInput: correspondenceSubjectInput,
		correspondenceReasonInput: correspondenceReasonInput,
		complaintInitiatedInput: complaintInitiatedInput,
		correspondenceNameInput: correspondenceNameInput,
		correspondenceEmailInput: correspondenceEmailInput,
		correspondencePhone1Input: correspondencePhone1Input,
		correspondencePhone2Input: correspondencePhone2Input,
		applyFormProps: applyFormProps,
		applyNavigationFooterProps: applyNavigationFooterProps,
	};
}

// Export parameters.
export {
	UseFormPopupAddPolicyNoteAdminParameters,
}

// Export results.
export {
	UseFormPopupAddPolicyNoteAdminResults,
}

export default useFormPopupAddPolicyNoteAdmin;